import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Eye, Send, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { authorAPI } from '../../lib/api';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Edit },
  pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Eye },
  approved: { label: 'Published', color: 'bg-green-100 text-green-800', icon: null },
  rejected: { label: 'Needs Revision', color: 'bg-red-100 text-red-800', icon: AlertCircle },
};

export default function AuthorArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await authorAPI.getArticles();
      setArticles(response.data);
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async (articleId, title) => {
    try {
      await authorAPI.submitForReview(articleId);
      toast.success(`"${title}" submitted for review`);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit article');
    }
  };

  const filteredArticles = articles.filter(article => {
    if (filter === 'all') return true;
    return article.status === filter;
  });

  const filterOptions = [
    { value: 'all', label: 'All Articles' },
    { value: 'draft', label: 'Drafts' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'approved', label: 'Published' },
    { value: 'rejected', label: 'Needs Revision' },
  ];

  return (
    <div data-testid="author-articles">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            My Articles
          </h1>
          <p className="text-muted-foreground">
            Manage your articles and track their status.
          </p>
        </div>
        <Link to="/author/articles/new">
          <Button data-testid="new-article-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Article
          </Button>
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === option.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {option.label}
            {option.value !== 'all' && (
              <span className="ml-2 text-xs">
                ({articles.filter(a => a.status === option.value).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Articles List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-background p-6 border border-border animate-pulse">
              <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="bg-background p-12 border border-border text-center">
          <p className="text-muted-foreground mb-4">
            {filter === 'all' 
              ? "You haven't written any articles yet."
              : `No articles with status "${filterOptions.find(f => f.value === filter)?.label}"`
            }
          </p>
          {filter === 'all' && (
            <Link to="/author/articles/new">
              <Button>Write Your First Article</Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredArticles.map((article) => {
            const status = statusConfig[article.status] || statusConfig.draft;
            const StatusIcon = status.icon;
            
            return (
              <div
                key={article.id}
                className="bg-background p-6 border border-border hover:border-accent/50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl font-semibold text-foreground">
                        {article.title}
                      </h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${status.color}`}>
                        {StatusIcon && <StatusIcon className="h-3 w-3" />}
                        {status.label}
                      </span>
                    </div>
                    <p className="text-muted-foreground mb-2 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>Category: {article.category}</span>
                      <span>Updated: {new Date(article.updated_at).toLocaleDateString()}</span>
                    </div>
                    
                    {/* Rejection Reason */}
                    {article.status === 'rejected' && article.rejection_reason && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm font-medium text-red-800 mb-1">Revision Requested:</p>
                        <p className="text-sm text-red-700">{article.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {/* Edit - available for draft and rejected */}
                    {['draft', 'rejected'].includes(article.status) && (
                      <Link to={`/author/articles/${article.id}/edit`}>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </Link>
                    )}
                    
                    {/* Submit for Review - available for draft and rejected */}
                    {['draft', 'rejected'].includes(article.status) && (
                      <Button
                        size="sm"
                        onClick={() => handleSubmitForReview(article.id, article.title)}
                        data-testid={`submit-${article.id}`}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Submit for Review
                      </Button>
                    )}
                    
                    {/* View - available for published articles */}
                    {article.status === 'approved' && (
                      <Link to={`/articles/${article.id}`} target="_blank">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          View Published
                        </Button>
                      </Link>
                    )}
                    
                    {/* Pending - show waiting message */}
                    {article.status === 'pending' && (
                      <span className="text-sm text-muted-foreground italic px-3 py-2">
                        Awaiting admin review...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
